import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { store } from 'redux/store';
import { setProductKey } from 'redux/reducers/productsReducer';
import { PermissionTypes } from 'helper/products';
import { useUserHasPermissionQuery } from 'services/api/products';
import { ResponseError } from 'services/api/base';
import { handleApiError } from 'helper/error';

// O backend sinaliza "empresa sem contrato para este produto" com code 402
// (base.messages.http_errors.402). Só esse caso é falta de acesso de verdade.
const SEM_CONTRATO = 402;

/**
 * Obtém a product_key do produto e libera a área protegida.
 *
 * Distingue "sem contrato" de "falha técnica" (issue #1842): antes, QUALQUER
 * erro — inclusive queda de rede, timeout ou 500 — mandava o usuário para
 * `/no-access`, a página comercial do produto que ele já paga. Agora só o 402
 * (ou uma resposta ok sem chave) leva para lá; o resto vai para o
 * `handleApiError`, que trata auth, reporta ao Sentry e mostra a tela de erro.
 */
export default function usePermissionCheck(product: PermissionTypes) {
  const navigate = useNavigate();
  const [keySaved, setKeySaved] = useState(false);
  const {
    data, isSuccess, isError, error,
  } = useUserHasPermissionQuery(product);

  useEffect(() => {
    if (isSuccess) {
      const key = data?.data?.product_key;

      if (key) {
        store.dispatch(setProductKey({ product, key }));
        setKeySaved(true);
      } else {
        navigate(`/no-access?product=${product}`);
      }
      return;
    }

    if (isError) {
      const responseError = error as ResponseError;

      if (Number(responseError?.status) === SEM_CONTRATO) {
        navigate(`/no-access?product=${product}`);
      } else {
        handleApiError(navigate, responseError);
      }
    }
  }, [product, data, isSuccess, isError, error, navigate]);

  return keySaved;
}
